/**
 * Caja, control del dinero y conciliación — esquemas del contrato (AF §8).
 *
 * Es el módulo eje del sistema: el cliente compró el proyecto porque detectó robo
 * interno, y **RN-CAJ-01** es la regla madre — ningún peso entra sin quedar atado, en el
 * mismo acto, a socio/venta + medio + sede + usuario + comprobante. El turno abierto se
 * exige únicamente cuando el medio mueve efectivo físico.
 *
 * Todo lo que la SPA, la app de socios y el kiosko mandan a `/api/cash-shifts`,
 * `/api/payments`, `/api/envelopes`, `/api/cash-differences`, `/api/cash-audits`,
 * `/api/bank-accounts`, `/api/bank-feed`, `/api/pos-*` y `/api/reconciliation` se valida
 * contra estos esquemas. Fuente única: el backend los importa, no los redefine.
 */
import { z } from "zod";
import { BANK_FEED_MATCH_STATUSES, BUSINESS_UNITS, CASH_DIFFERENCE_KINDS, CASH_DIFFERENCE_STATUSES, CASH_SHIFT_SLOTS, CASH_SHIFT_STATUSES, ENVELOPE_STATUSES, PAYER_KINDS, PAYMENT_METHODS, PAYMENT_STATUSES, POS_BATCH_STATUSES, POS_CARD_TYPES, POS_TERMINAL_MODES, REVERSAL_KINDS, TRANSFER_DECLARED_VIA_INPUT, } from "../enums.js";
import { dateISO, listQuery, moneySchema, objectId, pagination, reasonRequired } from "./base.js";
// ─────────────────────────────────────────────────────────────────────────────
// Turnos de caja (RN-CAJ-05..11)
// ─────────────────────────────────────────────────────────────────────────────
export const cashShiftSlot = z.enum(CASH_SHIFT_SLOTS);
export const cashShiftStatus = z.enum(CASH_SHIFT_STATUSES);
export const paymentMethod = z.enum(PAYMENT_METHODS);
export const payerKind = z.enum(PAYER_KINDS);
export const paymentStatus = z.enum(PAYMENT_STATUSES);
export const reversalKind = z.enum(REVERSAL_KINDS);
/**
 * RN-CAJ-07: apertura de turno. El efectivo inicial es CONTADO por el recepcionista; el
 * sistema lo compara contra el remanente del cierre anterior y, si no coinciden, abre
 * igual y deja la diferencia (la operación nunca queda rehén del faltante).
 * `shift` es opcional: si no viene, lo resuelve el corte horario de la sede
 * (`branch.cashShiftCut`, PV-59) — sábados, domingos y feriados con turno único.
 */
/** De qué caja se habla: la del gimnasio (default) o la del kiosko de la sede. */
export const businessUnit = z.enum(BUSINESS_UNITS);
export const openShiftBody = z.object({
    shift: cashShiftSlot.optional(),
    openingDeclaredCash: moneySchema.min(0),
    businessUnit: businessUnit.default("gym"),
});
/** Declaración por medio del cierre: lotes/cupones para posnet, contado para efectivo. */
export const declaredByMethod = z.object({
    method: paymentMethod,
    declared: moneySchema,
});
/**
 * RN-CAJ-09/10: **cierre a ciegas**. El body trae SÓLO lo declarado; el esperado viaja en
 * la respuesta, nunca antes. Por eso no hay un endpoint que devuelva el esperado de un
 * turno abierto cuando `blindMode` está activo: el recorte es de API.
 */
export const closeShiftBody = z.object({
    declaredCash: moneySchema.min(0),
    /** Lo que queda en caja como fondo de cambio del turno siguiente. */
    remainder: moneySchema.min(0),
    byMethod: z.array(declaredByMethod).default([]),
    note: z.string().trim().max(500).optional(),
    /** Detalle de billetes del sobre, opcional (RN-CAJ-13). */
    billDetail: z
        .array(z.object({ denomination: z.number().int().positive(), count: z.number().int().min(0) }))
        .optional(),
});
export const listShiftsQuery = listQuery.extend({
    from: dateISO.optional(),
    to: dateISO.optional(),
    shift: cashShiftSlot.optional(),
    status: cashShiftStatus.optional(),
    userId: objectId.optional(),
    /** Filtro de la pantalla «Historial de cierres»: con / sin diferencia. */
    withDifference: z.coerce.boolean().optional(),
    businessUnit: businessUnit.optional(),
});
export const currentShiftQuery = z.object({
    branchId: objectId.optional(),
    businessUnit: businessUnit.default("gym"),
});
// ─────────────────────────────────────────────────────────────────────────────
// Cobros (RN-CAJ-01..04, RN-CAJ-16..22, RN-CAJ-26)
// ─────────────────────────────────────────────────────────────────────────────
/** RN-CAJ-20: el descuento es una línea explícita, con motivo, sobre el precio de lista. */
export const discountInput = z
    .object({
    pct: z.number().min(0).max(100).optional(),
    amount: moneySchema.min(0).optional(),
    reason: reasonRequired,
})
    .refine((d) => d.pct !== undefined || d.amount !== undefined, {
    message: "El descuento necesita un porcentaje o un monto",
});
export const transferInput = z.object({
    bankAccountId: objectId,
    /** Número de operación del comprobante: no puede respaldar dos cobros (RN-CAJ-19bis). */
    reference: z.string().trim().min(3).max(120),
    receiptKey: z.string().trim().max(500).optional(),
    // `mercadopago` no se declara: lo registra el sistema al leer la cuenta vinculada.
    declaredVia: z.enum(TRANSFER_DECLARED_VIA_INPUT).default("reception"),
});
export const terminalInput = z.object({
    terminalId: objectId,
    couponNumber: z.string().trim().max(60).optional(),
    cardMasked: z.string().trim().max(30).optional(),
    installments: z.number().int().min(1).max(24).optional(),
});
/**
 * RN-CAJ-01: `chargeIds` no vacío **salvo** venta de kiosko (que trae `saleId`) o cobro de
 * convenio (que trae `settlementId`, RN-CAJ-26). No existe cobro sin concepto.
 */
export const recordPaymentBody = z
    .object({
    method: paymentMethod,
    /**
     * `0` sólo para cerrar conceptos que una promo o un descuento cubren enteros (la
     * clase de prueba bonificada, RN-PLA-22): el backend verifica que de verdad den $0.
     */
    amount: moneySchema.min(0),
    payer: z.object({
        kind: payerKind,
        id: objectId.optional(),
        name: z.string().trim().max(160).optional(),
    }),
    chargeIds: z.array(objectId).default([]),
    saleId: objectId.optional(),
    settlementId: objectId.optional(),
    discount: discountInput.optional(),
    /**
     * RN-PLA-05 / RN-PLA-21: aplicar el descuento que el socio tiene por su condición
     * —el que `whatIsOwed` ya restó de su `total`—, para que ese total sea cobrable tal
     * cual. **El importe no viaja**: lo recalcula el backend contra `benefits` y la lista
     * vigente. Default `true` porque esta ruta es la del mostrador, que cobra ese total;
     * un cobro que deliberadamente ignore la cortesía manda `false`.
     */
    applyAutomaticDiscount: z.boolean().default(true),
    /** Usar saldo a favor acumulado del socio para pagar parte o total de este cobro */
    useCreditBalance: z.boolean().default(false),
    promotionId: objectId.optional(),
    /** RN-PLA-20: el socio que refirió al que compra; obligatorio con una promo de referidos. */
    referrerMemberId: objectId.optional(),
    transfer: transferInput.optional(),
    terminal: terminalInput.optional(),
    debitAttemptId: objectId.optional(),
    receivedAt: dateISO.optional(),
})
    .refine((b) => b.chargeIds.length > 0 || b.saleId !== undefined || b.settlementId !== undefined, {
    message: "RN-CAJ-01: un cobro sin concepto no existe (chargeIds, saleId o settlementId)",
    path: ["chargeIds"],
})
    .refine((b) => b.method !== "transfer" || b.transfer !== undefined, {
    message: "Una transferencia necesita cuenta y número de operación (RN-CAJ-19)",
    path: ["transfer"],
});
/**
 * RN-CAJ-01/04 + RN-PLA-20: **el cálculo del cobro antes de cobrar**. Los mismos conceptos,
 * descuento y promo que va a llevar `POST /api/payments`; el server devuelve el importe que
 * después exige. `chargeIds` puede ir vacío: sirve igual para saber `canDiscount`.
 */
export const paymentQuoteBody = z.object({
    payer: z.object({
        kind: payerKind,
        id: objectId.optional(),
    }),
    chargeIds: z.array(objectId).default([]),
    discount: z
        .object({
        pct: z.number().min(0).max(100).optional(),
        amount: moneySchema.min(0).optional(),
    })
        .optional(),
    promotionId: objectId.optional(),
    /** RN-PLA-20: el socio que refirió al que compra; obligatorio con una promo de referidos. */
    referrerMemberId: objectId.optional(),
    /** Igual que en `recordPaymentBody`: la cortesía y el descuento familiar del socio. */
    applyAutomaticDiscount: z.boolean().default(true),
});
/** La clase de prueba por cobrar: se busca por nombre o documento (RN-PLA-22). */
export const trialOwedQuery = z.object({ q: z.string().trim().min(2).max(80) });
/** RN-CAJ-22: contra-asiento. Rol mínimo encargado de sede + motivo obligatorio (D5). */
export const reversePaymentBody = z.object({
    kind: reversalKind,
    reason: reasonRequired,
    /** Cobro correcto que reemplaza al anulado; queda encadenado al original. */
    replacement: z
        .object({
        method: paymentMethod,
        amount: moneySchema.min(1),
        chargeIds: z.array(objectId).default([]),
        transfer: transferInput.optional(),
        terminal: terminalInput.optional(),
        discount: discountInput.optional(),
    })
        .optional(),
});
export const listPaymentsQuery = listQuery.extend({
    from: dateISO.optional(),
    to: dateISO.optional(),
    method: paymentMethod.optional(),
    status: paymentStatus.optional(),
    shiftId: objectId.optional(),
    memberId: objectId.optional(),
    userId: objectId.optional(),
});
/** RN-CAJ-19: marcar un cobro como observado o incobrable (administración+, motivo). */
export const observePaymentBody = z.object({
    reason: reasonRequired,
    /** `revert` corta la habilitación y genera deuda (RN-CAJ-19.5, PV-63). */
    action: z.enum(["observe", "revert"]).default("observe"),
});
// ─────────────────────────────────────────────────────────────────────────────
// Terminal Payway (RN-CAJ-16/17/18)
// ─────────────────────────────────────────────────────────────────────────────
/**
 * RN-CAJ-16: el cobro nace en el sistema con el importe DE LISTA y viaja a la terminal.
 * Nadie tipea un importe en el posnet.
 */
export const createTerminalIntentBody = z
    .object({
    terminalId: objectId,
    amount: moneySchema.min(1),
    payer: z.object({
        kind: payerKind,
        id: objectId.optional(),
        name: z.string().trim().max(160).optional(),
    }),
    chargeIds: z.array(objectId).default([]),
    saleId: objectId.optional(),
    concept: z.string().trim().min(2).max(160),
    installments: z.number().int().min(1).max(24).default(1),
    discount: discountInput.optional(),
    promotionId: objectId.optional(),
    /** RN-PLA-20: el socio que refirió al que compra; obligatorio con una promo de referidos. */
    referrerMemberId: objectId.optional(),
})
    .refine((b) => b.chargeIds.length > 0 || b.saleId !== undefined, {
    message: "RN-CAJ-01: un cobro sin concepto no existe",
    path: ["chargeIds"],
});
export const terminalIntentStatuses = [
    "pending",
    "approved",
    "rejected",
    "canceled",
    "error",
];
export const terminalIntentStatus = z.enum(terminalIntentStatuses);
export const posTerminalMode = z.enum(POS_TERMINAL_MODES);
export const posCardType = z.enum(POS_CARD_TYPES);
/**
 * RN-CAJ-18: **cobro por posnet con el cupón del ticket**. Dos usos, según
 * `cash.posTerminalMode` de la sede:
 * - `manual` (el posnet no está integrado): es el cobro de todos los días. Lo carga
 *   recepción, sin motivo; los dueños reciben un resumen diario.
 * - `integrated`: es la **contingencia** de una terminal que no responde. Sólo encargado+
 *   y con motivo obligatorio (D5); alerta a los dueños en el momento.
 * El backend decide cuál rige: `reason` es opcional acá y obligatorio allá.
 */
export const manualTerminalPaymentBody = z
    .object({
    terminalId: objectId,
    couponNumber: z.string().trim().min(1).max(60),
    /** Número de lote del ticket, si recepción lo tiene a mano. El cruce es por cupón. */
    batchNumber: z.string().trim().min(1).max(40).optional(),
    cardType: posCardType.optional(),
    amount: moneySchema.min(1),
    cardMasked: z.string().trim().max(30).optional(),
    installments: z.number().int().min(1).max(24).default(1),
    payer: z.object({
        kind: payerKind,
        id: objectId.optional(),
        name: z.string().trim().max(160).optional(),
    }),
    chargeIds: z.array(objectId).default([]),
    saleId: objectId.optional(),
    /** La misma promo y el mismo descuento que el cobro por la terminal (RN-PLA-20/CAJ-20). */
    discount: discountInput.optional(),
    promotionId: objectId.optional(),
    /** RN-PLA-20: el socio que refirió al que compra; obligatorio con una promo de referidos. */
    referrerMemberId: objectId.optional(),
    /** Obligatorio sólo como contingencia del modo `integrated` (lo valida el backend). */
    reason: reasonRequired.optional(),
})
    .refine((b) => b.chargeIds.length > 0 || b.saleId !== undefined, {
    message: "RN-CAJ-01: un cobro sin concepto no existe",
    path: ["chargeIds"],
});
/** RN-CAJ-17: cierre de lote — por API de la terminal o por importación del cupón a cupón. */
export const closePosBatchBody = z.object({
    terminalId: objectId,
    batchNumber: z.string().trim().min(1).max(40),
    closedAt: dateISO.optional(),
    coupons: z
        .array(z.object({
        couponNumber: z.string().trim().min(1).max(60),
        amount: moneySchema.min(0),
    }))
        .optional(),
});
export const listPosBatchesQuery = pagination.extend({
    terminalId: objectId.optional(),
    branchId: objectId.optional(),
    status: z.enum(POS_BATCH_STATUSES).optional(),
});
export const upsertPosTerminalBody = z.object({
    branchId: objectId,
    bankAccountId: objectId,
    paywayTerminalId: z.string().trim().min(1).max(60),
    /** Francés 1 día hábil, ICBC 3 (default, PV-66). */
    settlementToleranceDays: z.number().int().min(0).max(30).default(1),
    active: z.boolean().default(true),
});
// ─────────────────────────────────────────────────────────────────────────────
// Sobres y retiros (RN-CAJ-13/14)
// ─────────────────────────────────────────────────────────────────────────────
export const prepareEnvelopeBody = z.object({
    amount: moneySchema.min(1),
    billDetail: z
        .array(z.object({ denomination: z.number().int().positive(), count: z.number().int().min(0) }))
        .optional(),
});
/**
 * RN-CAJ-14: retiro con **doble confirmación** — la sesión del dueño más su PIN, y el PIN
 * del recepcionista que entrega. Es la planilla firmada de hoy, digital.
 */
export const withdrawEnvelopeBody = z.object({
    ownerPin: z.string().trim().min(4).max(12),
    receptionistUserId: objectId,
    receptionistPin: z.string().trim().min(4).max(12),
});
export const listEnvelopesQuery = listQuery.extend({
    status: z.enum(ENVELOPE_STATUSES).optional(),
    from: dateISO.optional(),
    to: dateISO.optional(),
    shiftId: objectId.optional(),
});
// ─────────────────────────────────────────────────────────────────────────────
// Gastos menores desde caja (RN-CAJ-15)
// ─────────────────────────────────────────────────────────────────────────────
/**
 * RN-CAJ-15: el ticket es obligatorio. La única salida es la marca explícita `noReceipt`
 * con motivo — que el reporte de egresos lista aparte (RN-CON-13, caso borde §8).
 */
export const pettyExpenseBody = z
    .object({
    categoryCode: z.string().trim().min(2).max(60),
    amount: moneySchema.min(1),
    supplierId: objectId.optional(),
    receiptKey: z.string().trim().max(500).optional(),
    noReceiptReason: z.string().trim().min(5).max(500).optional(),
    notes: z.string().trim().max(500).optional(),
    /** Motivo de la aprobación de encargado+ cuando supera el tope (PV-67). */
    overCapReason: z.string().trim().min(5).max(500).optional(),
})
    .refine((b) => Boolean(b.receiptKey) || Boolean(b.noReceiptReason), {
    message: "RN-CAJ-15: el gasto necesita foto del ticket o la marca «sin comprobante» con motivo",
    path: ["receiptKey"],
});
// ─────────────────────────────────────────────────────────────────────────────
// Diferencias y arqueos (RN-CAJ-12, RN-CAJ-23)
// ─────────────────────────────────────────────────────────────────────────────
export const listDifferencesQuery = listQuery.extend({
    kind: z.enum(CASH_DIFFERENCE_KINDS).optional(),
    status: z.enum(CASH_DIFFERENCE_STATUSES).optional(),
    userId: objectId.optional(),
    from: dateISO.optional(),
    to: dateISO.optional(),
});
/** RN-CAJ-12: sólo administración+ cambia el estado, con motivo obligatorio (D5). */
export const resolveDifferenceBody = z.object({
    status: z.enum(["justified", "deducted", "assumed_by_house"]),
    reason: reasonRequired,
});
export const differenceRankingQuery = z.object({
    branchId: objectId.optional(),
    from: dateISO.optional(),
    to: dateISO.optional(),
});
/** RN-CAJ-23: el arqueo lo pide encargado+; bloquea SÓLO el efectivo mientras dura. */
export const startAuditBody = z.object({
    shiftId: objectId.optional(),
    countedByUserId: objectId,
});
/** El conteo es a ciegas: el esperado vuelve recién en la respuesta. */
export const declareAuditBody = z.object({ declaredCash: moneySchema.min(0) });
export const listAuditsQuery = listQuery.extend({
    from: dateISO.optional(),
    to: dateISO.optional(),
});
// ─────────────────────────────────────────────────────────────────────────────
// Cuentas bancarias, feed y conciliación (RN-CAJ-19, RN-CAJ-19bis)
// ─────────────────────────────────────────────────────────────────────────────
export const upsertBankAccountBody = z.object({
    bank: z.string().trim().min(2).max(80),
    alias: z.string().trim().min(3).max(60),
    cbu: z
        .string()
        .trim()
        .regex(/^\d{22}$/, "El CBU tiene 22 dígitos"),
    /** RN-CAJ-19.1: el ÚNICO alias/CBU que se le muestra al socio. */
    visibleToMembers: z.boolean().default(false),
    interbankingEnabled: z.boolean().default(true),
    active: z.boolean().default(true),
});
export const listBankFeedQuery = pagination.extend({
    accountId: objectId.optional(),
    matchStatus: z.enum(BANK_FEED_MATCH_STATUSES).optional(),
    from: dateISO.optional(),
    to: dateISO.optional(),
});
export const ingestFeedBody = z.object({
    accountId: objectId.optional(),
    from: dateISO.optional(),
    to: dateISO.optional(),
});
/** Plan B (Arquitectura §4.5): importación manual del extracto que el banco ya entrega. */
export const importExtractBody = z.object({
    accountId: objectId,
    /** Contenido del CSV tal cual, no una ruta: el archivo nunca toca el disco del server. */
    csv: z.string().min(1).max(5_000_000),
    delimiter: z.string().length(1).optional(),
});
export const proposeMatchesBody = z.object({
    accountId: objectId.optional(),
    /** Ventana de días hacia atrás sobre la que correr el matcher. */
    days: z.number().int().min(1).max(90).default(15),
});
export const confirmMatchBody = z.object({
    /** Diferencia explícita en un pago parcial o de más: NUNCA se redondea en silencio. */
    partialDiff: moneySchema.optional(),
});
export const confirmMatchesBatchBody = z.object({
    matchIds: z.array(objectId).min(1).max(200),
});
export const rejectMatchBody = z.object({ reason: reasonRequired });
/** RN-CAJ-19bis: N–M explícito, sólo manual, para el pago agrupado de un grupo familiar. */
export const manualMatchBody = z.object({
    feedEntryIds: z.array(objectId).min(1).max(50),
    paymentIds: z.array(objectId).min(1).max(50),
    partialDiff: moneySchema.optional(),
    reason: reasonRequired,
});
export const reconciliationQuery = z.object({
    branchId: objectId.optional(),
    accountId: objectId.optional(),
    from: dateISO.optional(),
    to: dateISO.optional(),
});
// ─────────────────────────────────────────────────────────────────────────────
// App de socios (RN-CAJ-19.2)
// ─────────────────────────────────────────────────────────────────────────────
/**
 * El socio declara su transferencia desde la app con el comprobante. Nace
 * `pending_reconciliation` y **lo habilita de inmediato**: la pendencia es bancaria, no de
 * derechos (RN-CAJ-19.3).
 */
export const appDeclareTransferBody = z.object({
    bankAccountId: objectId,
    reference: z.string().trim().min(3).max(120),
    amount: moneySchema.min(1),
    chargeIds: z.array(objectId).min(1),
    /** Key del comprobante ya subido a S3 con URL firmada. */
    receiptKey: z.string().trim().min(1).max(500),
});
//# sourceMappingURL=caja.js.map